import React, { useEffect, useState } from 'react';
import { Brain, BookOpen, ChevronLeft, ChevronRight, X, ArrowLeft } from 'lucide-react';
import { ContentItem } from '@/types/ai-learn.types';
import CourseNavigation from './CourseNavigation';
import AILiveChat from './AILiveChat';
import AILiveChatV2 from './AILiveChatV2';

interface TabbedSidebarProps {
  course: any;
  courseId: string;
  modules: any[];
  selectedModule: any;
  selectedModuleContent: any;
  selectedContent: ContentItem | null;
  expandedModules: { [key: string]: boolean };
  completedModules: Set<string>;
  loadingModuleContent: boolean;
  userLevel: number;
  userXP: number;
  learningStreak: number;
  courseProgress: number;
  isLastContentItem?: boolean;
  isCourseCompleted?: boolean;
  certificateIssued?: boolean;
  userEmail?: string;
  userName?: string;
  completingCourse?: boolean;
  showingCourseOverview?: boolean;
  moduleContentCache: { [moduleId: string]: any };
  progressData?: any;
  isLearner?: boolean;
  isScormCourse?: boolean;
  manualCompletionEnabled?: boolean;
  /** Assessments the course's completion config requires a pass on. */
  requiredAssessmentIds?: Set<string>;
  onModuleSelect: (module: any) => void;
  onContentItemSelect: (item: ContentItem) => void;
  onToggleModule: (moduleId: string) => void;
  onSetAllExpandedModules?: (expanded: { [key: string]: boolean }) => void;
  onCompleteCourse?: () => void;
  onShowCourseOverview?: () => void;
  onContentComplete?: (contentId: string) => void;
  onDownloadCertificate?: () => void;
  /**
   * Below lg the sidebar is not shown beside the player (a 384px column left
   * the lesson ~30px wide on a phone). Instead the page opens it on demand:
   * 'content' = bottom sheet with the course navigation, 'ai' = full-screen chat.
   */
  mobilePanel?: 'content' | 'ai' | null;
  onMobilePanelChange?: (panel: 'content' | 'ai' | null) => void;
}

/** True at Tailwind's lg breakpoint (1024px) and up. */
const useIsDesktop = (): boolean => {
  const query = '(min-width: 1024px)';
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    setIsDesktop(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
};

export default function TabbedSidebar({
  course,
  courseId,
  modules,
  selectedModule,
  selectedModuleContent,
  selectedContent,
  expandedModules,
  completedModules,
  loadingModuleContent,
  userLevel,
  userXP,
  learningStreak,
  courseProgress,
  isLastContentItem,
  isCourseCompleted,
  certificateIssued,
  userEmail,
  userName,
  completingCourse,
  showingCourseOverview,
  moduleContentCache,
  progressData,
  isLearner = true,
  isScormCourse = false,
  manualCompletionEnabled = true,
  requiredAssessmentIds,
  onModuleSelect,
  onContentItemSelect,
  onToggleModule,
  onSetAllExpandedModules,
  onCompleteCourse,
  onShowCourseOverview,
  onContentComplete,
  onDownloadCertificate,
  mobilePanel = null,
  onMobilePanelChange,
}: TabbedSidebarProps) {
  const [activeTab, setActiveTab] = useState<'course-content' | 'ai-assistant'>('course-content');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isDesktop = useIsDesktop();

  // Check if V2 WebSocket mode is enabled
  const useV2 = import.meta.env.VITE_AI_ASSISTANT_VERSION === 'v2';

  const closeMobilePanel = () => onMobilePanelChange?.(null);

  // Lock page scroll behind an open mobile panel
  useEffect(() => {
    if (isDesktop || !mobilePanel) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isDesktop, mobilePanel]);

  const renderCourseNavigation = (mobile: boolean) => (
    <CourseNavigation
      course={course}
      modules={modules}
      selectedModule={selectedModule}
      selectedModuleContent={selectedModuleContent}
      selectedContent={selectedContent}
      expandedModules={expandedModules}
      completedModules={completedModules}
      loadingModuleContent={loadingModuleContent}
      userLevel={userLevel}
      userXP={userXP}
      learningStreak={learningStreak}
      courseProgress={courseProgress}
      isLastContentItem={isLastContentItem}
      isCourseCompleted={isCourseCompleted}
      certificateIssued={certificateIssued}
      userEmail={userEmail}
      userName={userName}
      completingCourse={completingCourse}
      showingCourseOverview={showingCourseOverview}
      moduleContentCache={moduleContentCache}
      progressData={progressData}
      isLearner={isLearner}
      isScormCourse={isScormCourse}
      manualCompletionEnabled={manualCompletionEnabled}
      requiredAssessmentIds={requiredAssessmentIds}
      onModuleSelect={onModuleSelect}
      // On mobile, picking something to view closes the sheet so the learner
      // lands on it. Selecting/expanding a module keeps the sheet open so they
      // can pick a lesson inside it.
      onContentItemSelect={(item) => {
        onContentItemSelect(item);
        if (mobile) closeMobilePanel();
      }}
      onToggleModule={onToggleModule}
      onSetAllExpandedModules={onSetAllExpandedModules}
      onCompleteCourse={onCompleteCourse}
      onShowCourseOverview={onShowCourseOverview ? () => {
        onShowCourseOverview();
        if (mobile) closeMobilePanel();
      } : undefined}
      onContentComplete={onContentComplete}
      onDownloadCertificate={onDownloadCertificate}
    />
  );

  const renderAIChat = () => (
    useV2 ? (
      <AILiveChatV2
        course={course}
        courseId={courseId}
        currentModule={selectedModule}
        currentContent={selectedContent}
      />
    ) : (
      <AILiveChat
        course={course}
        courseId={courseId}
        currentModule={selectedModule}
        currentContent={selectedContent}
      />
    )
  );

  // ---- Phones & tablets (< lg): panels opened from the player toolbar ----
  if (!isDesktop) {
    if (mobilePanel === 'content') {
      return (
        <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true" aria-label="Course content">
          <div className="absolute inset-0 bg-black/40" onClick={closeMobilePanel} />
          <div className="relative bg-white rounded-t-2xl shadow-2xl flex flex-col h-[85vh] supports-[height:100dvh]:h-[85dvh] pb-[env(safe-area-inset-bottom)]">
            {/* Handle + header */}
            <div className="flex-shrink-0 pt-2 pb-1">
              <div className="mx-auto w-10 h-1.5 rounded-full bg-gray-300" />
            </div>
            <div className="flex-shrink-0 flex items-center justify-between px-4 pb-2 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-brand-blue-600" />
                Course content
              </h2>
              <button
                onClick={closeMobilePanel}
                className="p-2 -mr-2 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close course content"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              {renderCourseNavigation(true)}
            </div>
          </div>
        </div>
      );
    }

    if (mobilePanel === 'ai') {
      return (
        <div className="fixed inset-0 z-50 bg-white flex flex-col pb-[env(safe-area-inset-bottom)]" role="dialog" aria-modal="true" aria-label="AI Assistant">
          <div className="flex-shrink-0 flex items-center gap-2 px-2 py-2 border-b border-gray-200">
            <button
              onClick={closeMobilePanel}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
              aria-label="Back to lesson"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Brain className="w-4 h-4 text-brand-purple-600" />
              AI Assistant
            </h2>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            {renderAIChat()}
          </div>
        </div>
      );
    }

    return null;
  }

  // ---- Desktop (lg+): unchanged side panel ----
  return (
    <div className={`bg-white border-l border-gray-200 flex flex-col h-full transition-all duration-300 relative ${
      isCollapsed ? 'w-12' : 'w-96'
    }`}>
      {/* Collapse/Expand Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -left-3 top-1/2 -translate-y-1/2 z-20 bg-white border border-gray-200 rounded-full p-1.5 hover:bg-gray-50 shadow-md transition-all"
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? (
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-600" />
        )}
      </button>

      {!isCollapsed && (
        <>
      {/* Tab Headers */}
      <div className="flex border-b border-gray-200 bg-gray-50">
        <button
          className={`flex-1 px-2 py-1.5 text-xs font-medium transition-all duration-200 ${
            activeTab === 'course-content'
              ? 'text-brand-blue-600 border-b-2 border-brand-blue-600 bg-white shadow-sm'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
          onClick={() => setActiveTab('course-content')}
        >
          <div className="flex items-center gap-1 justify-center">
            <BookOpen className="w-3 h-3" />
            <span>Content</span>
          </div>
        </button>
        <button
          className={`flex-1 px-2 py-1.5 text-xs font-medium transition-all duration-200 ${
            activeTab === 'ai-assistant'
              ? 'text-brand-purple-600 border-b-2 border-brand-purple-600 bg-white shadow-sm'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
          onClick={() => setActiveTab('ai-assistant')}
        >
          <div className="flex items-center gap-1 justify-center">
            <Brain className="w-3 h-3" />
            <span>AI Assistant</span>
          </div>
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'course-content' && renderCourseNavigation(false)}
        {activeTab === 'ai-assistant' && renderAIChat()}
      </div>
      </>
      )}
    </div>
  );
}